import React from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Tooltip,
} from "@mui/material";
import Textarea from "@mui/joy/Textarea";
import InputLabel from "@mui/material/InputLabel";
import {
  addDisposition,
  updateDisposition,
} from "../../../../../../api/disposition";
import { TextInputComponent } from "../../../../../../common/components";
import DatePickerComponent from "../../../../../../common/components/date-picker/date-picker-compoenent";
import dayjs from "dayjs";
import CloseIcon from "../../../../../../assets/icons/closeIcon.svg";
import "./company-dashboard-initiative-drawer-add-disposition.scss";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

const CompanyDashboardInitiativeDrawerAddDisposition: any = (props: any) => {
  const {
    isOpenAddPopup,
    onCancel,
    title,
    editData,
    fetchInitiativeById,
    setLoading,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
    initiativeId,
  } = props;

  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");

  const userName =
    userDetails?.firstName || "" + " " + userDetails?.lastName || "";

  const validationSchema = () =>
    Yup.object().shape({
      asOf: Yup.date()
        .nullable()
        .transform((value, originalValue) => {
          return originalValue === "" ? null : value;
        })
        .typeError("Please select a valid As Of date")
        .required("As Of date is required"),
      openedBy: Yup.string().required("Opened By is required"),
      description: Yup.string()
        .required("Description is required")
        .max(1000, "Description cannot exceed 1000 characters"),
      percentageComplete: Yup.number()
        .typeError("Percentage complete must be a number")
        .min(0, "Percentage complete cannot be less than 0")
        .max(100, "Percentage complete cannot be more than 100")
        .required("Percentage complete is required"),
    });

  const handleCreateDisposition = async (payload: any) => {
    setLoading(true);
    try {
      await (editData?.id
        ? updateDisposition(editData?.id, payload)
        : addDisposition(payload));
      fetchInitiativeById();
      if (editData?.id) {
        setAlertMessage("Initiative updated successfully");
      } else {
        setAlertMessage("Initiative created successfully");
      }
      setAlertType("success");
      setAlertOpen(true);
      setLoading(false);
      onCancel();
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create Initiative",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  return (
    // >
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`company-dashboard-initiative-drawer-add-disposition`}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel(false);
      }}
      sx={{
        "& .MuiPaper-root": {
          backgroundColor: "#f7f8fb",
        },
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">{title}</div>
          <div className="image-wrapper">
            <button
              style={{
                border: "none",
                background: "none",
                padding: 0,
              }}
              onClick={onCancel}
            >
              <img
                src={CloseIcon}
                className="close-icon"
                style={{ cursor: "pointer" }}
                alt="close"
              />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <Formik
            initialValues={{
              asOf: dayjs(editData?.asOf) || null,
              openedBy: editData?.openedBy || userName || "",
              percentageComplete: editData?.percentageComplete || "",
              description: editData?.description || "",
            }}
            validationSchema={validationSchema}
            validateOnChange={true}
            enableReinitialize={true}
            onSubmit={(values) => {
              const newDisposition = {
                asOf: values.asOf,
                description: values.description,
                percentageComplete: values.percentageComplete,
                initiativeId: initiativeId,
              };
              handleCreateDisposition(newDisposition);
            }}
          >
            {({
              values,
              setFieldValue,
              touched,
              errors,
              isValid,
              submitCount,
            }) => {
              const charCount = values.description?.length || 0;
              const remaining = 1000 - charCount;
              return (
                <Form>
                  <OverlayScrollbarsComponent
                    className="create-lead-capture-popup-confirmation-description-checkbox-input"
                    style={{
                      maxHeight: "520px",
                      overflow: "auto",
                      overflowX: "hidden",
                      width: "460px",
                    }}
                    options={{
                      scrollbars: {
                        theme: "os-theme-dark",
                        autoHide: "scroll",
                        autoHideDelay: 400,
                      },
                    }}
                  >
                    <div className="form-group">
                      <DatePickerComponent
                        fieldName={"asOf"}
                        label="As Of"
                        value={values?.asOf}
                        onChange={(e: any) => {
                          setFieldValue("asOf", e);
                        }}
                        disabled={!!editData?.id}
                        maxDate={dayjs()}
                      />
                    </div>
                    <div className="form-group">
                      <TextInputComponent
                        type="text"
                        fieldName="Reported By"
                        placeholder="Reported By"
                        label="Reported By"
                        optional={false}
                        disabled={true}
                        value={values.openedBy}
                      />
                      {touched?.openedBy && (
                        <ErrorMessage
                          name={"openedBy"}
                          component="div"
                          className="error-message"
                        />
                      )}
                    </div>
                    <div className="form-group">
                      <div className={`form-text-input-component`}>
                        <InputLabel required={true} htmlFor={"description"}>
                          Description
                        </InputLabel>
                        <FormControl>
                          <Textarea
                            color="neutral"
                            disabled={false}
                            minRows={2}
                            size="lg"
                            variant="outlined"
                            value={values.description}
                            onChange={(e) =>
                              setFieldValue("description", e?.target?.value)
                            }
                            placeholder="Enter description here"
                          />
                        </FormControl>
                        {touched.description && errors.description && (
                          <div className="error-message">
                            {errors.description as string}
                          </div>
                        )}
                        <div
                          className={`char-counter ${
                            remaining < 0 ? "error-message" : ""
                          }`}
                        >
                          {charCount} / 1000 characters
                        </div>
                      </div>
                    </div>
                    <div className="form-group">
                      <TextInputComponent
                        type="text"
                        fieldName="percentageComplete"
                        label="Percentage Reported"
                        placeholder="0.00"
                        disabled={false}
                        isLocked={true}
                        lockedIcons={
                          <i
                            className="fa fa-percent"
                            style={{ color: "#0000008a", fontWeight: "normal" }}
                          />
                        }
                        value={values.percentageComplete}
                        onChange={(e: any) => {
                          if (
                            e?.target?.value === "" ||
                            /^(100(\.0{1,2})?|\d{1,2}(\.\d{0,2})?)%?$/.test(
                              e?.target?.value,
                            )
                          ) {
                            setFieldValue(
                              "percentageComplete",
                              e?.target?.value,
                            );
                          }
                        }}
                        maxlength="50"
                      />
                    </div>
                  </OverlayScrollbarsComponent>
                  <DialogActions>
                    <div className="action-butn-wrapper">
                      <button
                        onClick={onCancel}
                        className="cancel-btn"
                        type="button"
                      >
                        Cancel
                      </button>
                      {submitCount > 0 && !isValid ? (
                        <Tooltip
                          title="Please resolve all errors before submitting."
                          placement="top"
                        >
                          <span>
                            <button
                              autoFocus
                              className="action-btn"
                              type="submit"
                              disabled
                              style={{
                                opacity: 0.5,
                                cursor: "not-allowed",
                              }}
                            >
                              {title === "Add Disposition" ? "Create" : "Save"}
                            </button>
                          </span>
                        </Tooltip>
                      ) : (
                        <button
                          autoFocus
                          className="action-btn"
                          type="submit"
                          style={{
                            opacity: 1,
                            cursor: "pointer",
                          }}
                        >
                          {title === "Add Disposition" ? "Create" : "Save"}
                        </button>
                      )}
                    </div>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardInitiativeDrawerAddDisposition;
